import React from 'react';
import { Button, Card, Tooltip as AntdTooltip } from 'antd';
import * as BizCharts from 'bizcharts';
import { DownloadOutlined } from '@ant-design/icons';
import {
	AXIS_Y_LINE_COLOR,
	AXIS_X_LINE_COLOR,
	CHART_PRIMARY_COLOR,
} from './LifeCycleAnalyticsConstants';
import {
	formatDurationShort,
	parseSafeFloat,
	roundToOneDecimalPlace,
} from './LifeCycleAnalyticsUtils';

const { Chart, Axis, Geom, Tooltip } = BizCharts as any;

const MetricChart = ({
	chartColor,
	chartMetric: _chartMetric,
	activeMetricConfig,
	chartMetricSummaryValue,
	chartMetricDeltaElement,
	monthlyMetricChartData,
	selectedPriorityCard,
	formatMetricAxisLabel,
	exportMetricChartCsv,
}) => {
	if (!activeMetricConfig) {
		return null;
	}

	const lineColor = chartColor;
	return (
		<Card
			className="print:!h-auto print:!min-h-0 print:!break-inside-avoid print:[&_.ant-card-body]:!break-inside-avoid print:[&_.ant-card-body]:!px-2 print:[&_.ant-card-body]:!pb-3 print:[&_.ant-card-body]:!pt-4"
			bodyStyle={{ padding: '57px 36px 40px 16px' }}
			title={
				<div className="flex min-w-0 flex-nowrap items-center gap-3">
					<div className="flex items-baseline gap-1">
						<span className="h5medium text-character-title/85">
							{activeMetricConfig.label}:{' '}
							{activeMetricConfig.isTimeBased
								? formatDurationShort(chartMetricSummaryValue)
								: `${roundToOneDecimalPlace(chartMetricSummaryValue)}%`}
						</span>
						{chartMetricDeltaElement}
					</div>
				</div>
			}
			extra={
				<div className="flex items-center gap-2">
					{selectedPriorityCard && (
						<AntdTooltip title={selectedPriorityCard.woPriorityName}>
							<span
								className="inline-block min-w-0 max-w-[250px] truncate rounded-2xl px-3 py-1 align-middle text-sm font-medium leading-[22px]"
								style={{
									color: selectedPriorityCard.color,
									backgroundColor: selectedPriorityCard.bgColor,
								}}
							>
								{selectedPriorityCard.woPriorityName}
							</span>
						</AntdTooltip>
					)}
					<AntdTooltip title="Download">
						<Button
							className="print:!hidden"
							icon={<DownloadOutlined />}
							onClick={exportMetricChartCsv}
						/>
					</AntdTooltip>
				</div>
			}
		>
			<div className="w-full min-w-0 print:!break-inside-avoid">
				<Chart
					height={360}
					data={monthlyMetricChartData}
					autoFit={true}
					forceFit={true}
					theme="openwrench"
					scale={{
						metricValue: {
							min: 0,
							nice: true,
						},
					}}
				>
					<Axis
						name="monthLabel"
						title={{
							text: 'Month',
							style: { fontSize: 12, fill: 'rgba(0,0,0,0.45)' },
							offset: 40,
						}}
						line={{ style: { stroke: AXIS_X_LINE_COLOR } }}
						tickLine={null}
						label={{
							style: { fontSize: 12, fill: 'rgba(0,0,0,0.45)' },
						}}
					/>
					<Axis
						name="metricValue"
						title={{
							text: activeMetricConfig.axisTitle,
							style: { fontSize: 12, fill: 'rgba(0,0,0,0.45)' },
							offset: 80,
						}}
						label={{
							formatter: formatMetricAxisLabel,
							style: { fontSize: 12, fill: 'rgba(0,0,0,0.45)' },
						}}
						line={{ style: { stroke: AXIS_Y_LINE_COLOR } }}
						tickLine={null}
						grid={{
							line: {
								style: {
									stroke: AXIS_Y_LINE_COLOR,
									lineDash: [4, 4],
								},
							},
						}}
					/>
					<Tooltip crosshairs={{ type: 'y' }} />
					<Geom
						type="line"
						position="monthLabel*metricValue"
						color={lineColor}
						size={2}
						tooltip={[
							'monthFull*metricValue*metricRaw',
							(monthFull, metricVal, metricRaw) => ({
								title: monthFull,
								name: activeMetricConfig.label,
								value: activeMetricConfig.isTimeBased
									? formatDurationShort(parseSafeFloat(metricRaw))
									: `${roundToOneDecimalPlace(parseSafeFloat(metricVal))}%`,
							}),
						]}
					/>
					<Geom
						type="point"
						position="monthLabel*metricValue"
						color={lineColor}
						size={5}
						shape={'circle'}
						style={{
							fill: '#fff',
							stroke: lineColor,
							lineWidth: 2,
						}}
						tooltip={false}
					/>
				</Chart>
			</div>
		</Card>
	);
};

export default MetricChart;
